// composables/usePlaidLink.js
import { ref } from "vue";
import { usePurchaseFlowStore } from "../stores/usePurchaseFlowStore";
import { useAuthStore } from "../stores/useAuthStore";
import { useAnalytics } from "./useAnalytics";

// In cases where there are multiple BankCard instances,
// this shared handler ensures that each instance does not need to independently
// fetch a link token and create a new Plaid handler,
// reducing redundant network requests and improving performance.
const handler = ref(null);
const isLoading = ref(false);
const isReady = ref(false);
const error = ref(null);

const pendingCallback = ref(null);
const pendingAccountType = ref(null);

let initPromise = null;

export function usePlaidLink() {
  const purchaseFlowStore = usePurchaseFlowStore();
  const authStore = useAuthStore();
  const { bankAccountAdded } = useAnalytics();

  async function initialize() {
    if (handler.value) return;
    if (initPromise) return initPromise;

    initPromise = (async () => {
      try {
        error.value = null;

        const headers = {
          "content-type": "application/json",
          "Access-Control-Allow-Origin": "*",
          session_id: authStore.current_user.session_id,
          user_id: authStore.current_user.id,
        };

        const response = await fetch(
          `${import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT}/call-plaid-endpoint`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              endpoint: "generate_link_token",
              data: { user_id: authStore.current_user.id },
            }),
          }
        );

        const link_token_data = await response.json();
        const link_token = link_token_data?.link_token || "";

        handler.value = Plaid.create({
          clientName: "Phoenix Capital Group",
          accountSubtypes: {
            depository: ["checking", "savings"],
          },
          token: link_token,
          onSuccess: async (public_token, metadata) => {
            isLoading.value = true;

            try {
              const headers = {
                "content-type": "application/json",
                "Access-Control-Allow-Origin": "*",
                session_id: authStore.current_user.session_id,
                user_id: authStore.current_user.id,
              };

              const result = await fetch(
                `${import.meta.env.VITE_INVESTMENT_SERVICES_ENDPOINT}/call-plaid-endpoint`,
                {
                  method: "POST",
                  headers,
                  body: JSON.stringify({
                    endpoint: "item_public_token_exchange",
                    data: {
                      public_token,
                      user_id: authStore.current_user.id,
                      accountType: pendingAccountType.value,
                    },
                  }),
                }
              );

              const accessToken = await result.json();
              await purchaseFlowStore.getAccountsFromPlaid(pendingAccountType.value, accessToken);
              bankAccountAdded(metadata?.accounts?.[0]?.subtype ?? null);

              if (pendingCallback.value) {
                pendingCallback.value(accessToken, metadata);
              }
            } catch (err) {
              console.error(err);
              error.value = err;
            } finally {
              isLoading.value = false;
              pendingCallback.value = null;
              pendingAccountType.value = null;
            }
          },
          onLoad: () => {},
          onExit: (err, metadata) => {
            // Clear pending state if user exits
            pendingCallback.value = null;
            pendingAccountType.value = null;
          },
          onEvent: (eventName, metadata) => {},
        });

        isReady.value = true;
      } catch (err) {
        console.error(err);
        error.value = err;
        initPromise = null;
      }
    })();

    return initPromise;
  }

  function open(accountType, onSuccess) {
    if (!handler.value) {
      console.warn("Plaid handler not initialized");
      return;
    }

    // Store for use in onSuccess callback
    pendingAccountType.value = accountType;
    pendingCallback.value = onSuccess;

    handler.value.open();
  }

  return {
    initialize,
    open,
    isLoading,
    isReady,
    error,
  };
}
